import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { formatDayMonthYear } from "@/lib/dates";
import { purgeDate } from "@/lib/deletionGrace";

/// A bell badge is missable and a member who never opens it loses their work
/// with no notice at all. This sits above every surface of a scheduled org
/// until the window closes or someone cancels. No dismiss. A doomed SPACE
/// needs no banner: it is in the Trash, and the rail no longer shows it.
///
/// It states the fact and names the door; it is not itself a door. A cancel
/// affects the whole org, so it belongs where the rest of that decision lives
/// and not one click from every surface.
export const OrgDeletionBanner: React.FC = () => {
  const org = useOrgContextStore(
    useShallow((s) => s.orgs.find((o) => o.org_id === s.activeOrgId)),
  );
  if (!org?.deletion_scheduled_at) return null;

  const isGovernor = org.role === "owner" || org.role === "org_admin";
  const started = !!org.deletion_started_at;
  const closes = org.purge_at
    ? formatDayMonthYear(org.purge_at)
    : purgeDate(org.deletion_scheduled_at);

  return (
    <div className="org-deletion-banner" role="alert">
      <div className="banner-dot" />
      <span>
        {started ? (
          <>
            <span className="font-medium">{org.name}</span> is being deleted.
            This can no longer be stopped.
          </>
        ) : org.is_personal ? (
          // D55: the personal org goes with the account, on the account's
          // clock — and the account settings own the cancel.
          <>
            Your account is scheduled for deletion on {closes}. Cancel it in
            account settings.
          </>
        ) : (
          <>
            <span className="font-medium">{org.name}</span> is scheduled for
            deletion on {closes}.{" "}
            {isGovernor
              ? "Cancel it in organization settings."
              : "An owner or admin can cancel it."}
          </>
        )}
      </span>
    </div>
  );
};
